import { useCallback, useEffect } from 'react'

export interface KeyboardShortcut {
  /** Key(s) to listen for. Can be a single key or array of keys */
  key: string | string[]
  /** Callback function to execute when shortcut is triggered */
  callback: () => void
  /** Whether Ctrl/Cmd key should be pressed (cross-platform) */
  ctrlOrCmd?: boolean
  /** Whether Ctrl key should be pressed (Windows/Linux specific) */
  ctrl?: boolean
  /** Whether Cmd key should be pressed (macOS specific) */
  cmd?: boolean
  /** Whether Shift key should be pressed */
  shift?: boolean
  /** Whether Alt key should be pressed */
  alt?: boolean
  /** Whether to prevent default browser behavior (default: true) */
  preventDefault?: boolean
  /** Whether shortcut is currently enabled (default: true) */
  enabled?: boolean
  /** Description of the shortcut for documentation purposes */
  description?: string
}

interface UseKeyboardShortcutsOptions {
  /** Whether shortcuts are globally enabled (default: true) */
  enabled?: boolean
  /** Target element to attach listeners to (default: window) */
  target?: EventTarget | null
}

/**
 * Custom hook for handling keyboard shortcuts
 *
 * @example
 * ```typescript
 * useKeyboardShortcuts([
 *   {
 *     key: '+',
 *     ctrlOrCmd: true,
 *     callback: () => zoomIn(),
 *     description: 'Zoom in'
 *   },
 *   {
 *     key: ['-', 'NumpadSubtract'],
 *     ctrlOrCmd: true,
 *     callback: () => zoomOut(),
 *     description: 'Zoom out'
 *   },
 *   {
 *     key: 'ArrowLeft',
 *     ctrlOrCmd: true,
 *     callback: () => toggleEditor(),
 *     description: 'Toggle editor panel'
 *   }
 * ])
 * ```
 */
export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[], options: UseKeyboardShortcutsOptions = {}) => {
  const { enabled = true, target = typeof window !== 'undefined' ? window : null } = options

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return

      // Check each shortcut
      for (const shortcut of shortcuts) {
        if (shortcut.enabled === false) continue

        const {
          key,
          callback,
          ctrlOrCmd = false,
          ctrl = false,
          cmd = false,
          shift = false,
          alt = false,
          preventDefault = true,
        } = shortcut

        // Check modifier keys
        const isCtrlOrCmd = ctrlOrCmd && (event.ctrlKey || event.metaKey)
        const isCtrl = ctrl && event.ctrlKey
        const isCmd = cmd && event.metaKey
        const isShift = shift && event.shiftKey
        const isAlt = alt && event.altKey

        // Check if modifiers match
        const modifiersMatch =
          (ctrlOrCmd ? isCtrlOrCmd : true) &&
          (ctrl ? isCtrl : !ctrl || !event.ctrlKey) &&
          (cmd ? isCmd : !cmd || !event.metaKey) &&
          (shift ? isShift : !shift || !event.shiftKey) &&
          (alt ? isAlt : !alt || !event.altKey)

        if (!modifiersMatch) continue

        // Check if key matches
        const keys = Array.isArray(key) ? key : [key]
        const keyMatches = keys.some((k) => {
          // Handle special cases for key matching
          if (k === '+' && (event.key === '+' || event.key === '=' || event.code === 'NumpadAdd')) {
            return true
          }
          if (k === '-' && (event.key === '-' || event.code === 'NumpadSubtract')) {
            return true
          }
          return event.key === k || event.code === k
        })

        if (keyMatches && modifiersMatch) {
          if (preventDefault) {
            event.preventDefault()
          }
          callback()
          break // Stop checking other shortcuts once we find a match
        }
      }
    },
    [shortcuts, enabled]
  )

  useEffect(() => {
    if (!target || !enabled) return

    target.addEventListener('keydown', handleKeyDown as EventListener)
    return () => target.removeEventListener('keydown', handleKeyDown as EventListener)
  }, [target, enabled, handleKeyDown])
}

// Utility function to create common shortcut patterns
export const createShortcut = (
  key: string | string[],
  callback: () => void,
  options: Partial<Omit<KeyboardShortcut, 'key' | 'callback'>> = {}
): KeyboardShortcut => ({
  key,
  callback,
  ctrlOrCmd: false,
  preventDefault: true,
  enabled: true,
  ...options,
})

// Common shortcut presets
export const SHORTCUT_PRESETS = {
  ctrlOrCmd: (key: string | string[], callback: () => void, description?: string): KeyboardShortcut =>
    createShortcut(key, callback, { ctrlOrCmd: true, description }),

  ctrlOrCmdShift: (key: string | string[], callback: () => void, description?: string): KeyboardShortcut =>
    createShortcut(key, callback, { ctrlOrCmd: true, shift: true, description }),

  ctrlOrCmdAlt: (key: string | string[], callback: () => void, description?: string): KeyboardShortcut =>
    createShortcut(key, callback, { ctrlOrCmd: true, alt: true, description }),

  zoomIn: (callback: () => void): KeyboardShortcut =>
    createShortcut(['+', '=', 'NumpadAdd'], callback, {
      ctrlOrCmd: true,
      description: 'Zoom in',
    }),

  zoomOut: (callback: () => void): KeyboardShortcut =>
    createShortcut(['-', 'NumpadSubtract'], callback, {
      ctrlOrCmd: true,
      description: 'Zoom out',
    }),
}
